import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ArrowsUpDownIcon,
  InformationCircleIcon,
  MinusIcon,
  PlusIcon,
} from '@heroicons/react/20/solid'
import { useWallet } from '@solana/wallet-adapter-react'
import store from 'stores/store'
import { countLeadingZeros, floorToDecimal } from 'utils/numbers'
import { PublicKey } from '@solana/web3.js'
import {
  useInvalidateWalletTokens,
  useWalletTokens,
} from 'hooks/useWalletTokens'
import { sleep } from 'utils'
import InlineNotification from '@components/shared/InlineNotification'
import ConnectButton from '@components/shared/ConnectButton'
import { TOKEN_PROGRAM_ID } from '@solana/spl-token'
import {
  NumericFormat,
  NumberFormatValues,
  SourceInfo,
} from 'react-number-format'
import LimitSellInput from './LimitSellInput'
import LimitBuyInput from './LimitBuyInput'
import { walletBalanceForToken } from 'utils/tokens'
import { calcMarketDecimals } from 'hooks/useManifestMarket'
import ImageWithFallback from '@components/image/ImageWithFallback'
import { placeLimitOrder } from 'utils/limitOrders'
import { useOpenOrders } from 'hooks/useOpenOrders'
import { useUnsettledBalances } from 'hooks/useUnsettledBalances'
import { useMarketTokensWithPrices } from 'hooks/useMarketTokens'
import Decimal from 'decimal.js'
import Tooltip from '@components/shared/Tooltip'
import { SizeButtons } from '@components/swap/SellInput'
import { useTranslation } from 'next-i18next'

//
const set = store.getState().set

const ABS_PRICE_CHANGE_VALUES = ['1', '2', '5', '10', '25']

const LimitForm = ({
  preserveInitialPrice = false,
}: {
  preserveInitialPrice?: boolean
}) => {
  const { t } = useTranslation(['trade', 'messages'])
  const limitForm = store((s) => s.trade.limitForm)
  const [animateSwitchArrow, setAnimateSwitchArrow] = useState(0)
  const [error, setError] = useState('')
  const [initPriceWasSet, setInitPriceWasSet] = useState(false)
  const wallet = useWallet()
  const { walletTokens } = useWalletTokens()
  const { baseMintPrice, quoteMintPrice, refetchMarketPrices } =
    useMarketTokensWithPrices()

  const inputToken = store((s) => s.swap.inputToken)
  const outputToken = store((s) => s.swap.outputToken)
  const baseToken = store((s) => s.market.baseToken)
  const quoteToken = store((s) => s.market.quoteToken)
  const manifestMarket = store((s) => s.market.manifestMarket)
  const invalidateWalletTokens = useInvalidateWalletTokens()
  const { refetchUnsettledBalances } = useUnsettledBalances()
  const { refetchOpenOrders } = useOpenOrders()

  const isBid = inputToken.mint === quoteToken.mint
  const priceChangeValues = isBid
    ? ABS_PRICE_CHANGE_VALUES.map((v) => `${+v * -1}`)
    : ABS_PRICE_CHANGE_VALUES

  const lastPrice = useMemo(() => {
    if (!baseMintPrice || !quoteMintPrice) return 0
    return baseMintPrice / quoteMintPrice
  }, [baseMintPrice, quoteMintPrice])

  const priceDifferencePercentage = useMemo(() => {
    if (!limitForm.price || !lastPrice) return 0
    return ((+limitForm.price - lastPrice) / lastPrice) * 100
  }, [limitForm.price, lastPrice])

  const tickDecimals = useMemo(() => {
    const priceDecimals = lastPrice
      ? lastPrice < 0.1
        ? countLeadingZeros(lastPrice)
        : 2
      : 2

    const marketDecimals = manifestMarket?.tickDecimals
      ? manifestMarket.tickDecimals
      : lastPrice
        ? calcMarketDecimals(lastPrice)[0]
        : 2

    return marketDecimals + priceDecimals
  }, [manifestMarket, lastPrice])

  // set init price
  useEffect(() => {
    if (initPriceWasSet) {
      return
    }

    if (lastPrice && !preserveInitialPrice) {
      set((state) => {
        state.trade.limitForm.price = String(lastPrice)
      })

      store
        .getState()
        .actions.setCalculatedLimitFormOutputAmount(
          store.getState().trade.limitForm.inputAmount,
          String(lastPrice),
          tickDecimals,
        )
    }

    if (lastPrice || preserveInitialPrice) {
      setInitPriceWasSet(true)
    }
  }, [initPriceWasSet, lastPrice, preserveInitialPrice, tickDecimals])

  // reset init price
  useEffect(() => {
    if (baseToken.mint || quoteToken.mint) {
      setInitPriceWasSet(false)
    }
  }, [baseToken.mint, quoteToken.mint])

  // const feeParams = calcFeeParams(
  //   bestRoute?.outAmount?.toString() || '0',
  //   !!localReferrer,
  //   outputToken?.decimals ?? 6,
  //   outputToken?.latest_price_usd,
  //   solPrice,
  //   outputMint as string,
  //   outputToken?.token_program || TOKEN_PROGRAM_ID.toBase58(),
  // )

  // const { vault: referrerVault } = useReferrerVault(
  //   feeParams?.feeMint.toBase58(),
  //   feeParams?.feeProgram.toBase58(),
  // )

  const [walletMax, sellDecimals] = useMemo(() => {
    if (!inputToken.mint) return ['0', 6]
    const walletBalance = walletBalanceForToken(walletTokens, inputToken.mint)
    const max = floorToDecimal(
      walletBalance.balance,
      walletBalance.decimals,
    ).toFixed()
    return [max, walletBalance.decimals]
  }, [walletTokens, inputToken.mint])

  const handleSwitchTokens = useCallback(() => {
    const inputToken = store.getState().swap.inputToken
    const outputToken = store.getState().swap.outputToken

    set((state) => {
      state.swap.inputToken = outputToken
      state.swap.outputToken = inputToken
      state.trade.limitForm.inputAmount = ''
      state.trade.limitForm.outputAmount = ''
    })
    setAnimateSwitchArrow(
      (prevAnimateSwitchArrow) => prevAnimateSwitchArrow + 1,
    )
    void refetchMarketPrices()
  }, [refetchMarketPrices])

  const updatePriceAndCalculateOutput = useCallback(
    (priceValue: string) => {
      set((state) => {
        state.trade.limitForm.price = priceValue
      })
      store
        .getState()
        .actions.setCalculatedLimitFormOutputAmount(
          limitForm.inputAmount,
          priceValue,
          tickDecimals,
        )
    },
    [limitForm, tickDecimals],
  )

  const handlePriceChange = useCallback(
    (e: NumberFormatValues, info: SourceInfo) => {
      if (info.source !== 'event') return
      const value = e.value === '.' ? '0.' : e.value
      updatePriceAndCalculateOutput(value)
    },
    [updatePriceAndCalculateOutput],
  )

  const placeOrder = useCallback(async () => {
    if (!wallet.publicKey || !wallet.signTransaction) {
      setError(t('messages:errors.wallet-not-connected'))
      return
    }
    const connection = store.getState().connection
    const amount = isBid
      ? limitForm.outputAmount?.replace(/,/g, '')
      : limitForm.inputAmount?.replace(/,/g, '')
    await placeLimitOrder(
      new PublicKey(baseToken.mint),
      baseToken?.token_program || TOKEN_PROGRAM_ID.toBase58(),
      baseToken?.decimals ?? 6,
      new PublicKey(quoteToken.mint),
      quoteToken?.token_program || TOKEN_PROGRAM_ID.toBase58(),
      quoteToken?.decimals ?? 6,
      connection,
      wallet.publicKey,
      wallet.signTransaction,
      isBid,
      amount,
      limitForm.price,
      !!manifestMarket,
      {
        transactionSent: t('messages:transaction-sent'),
        waitingForConfirmation: t('messages:waiting-for-confirmation'),
        transactionConfirmed: t('messages:transaction-confirmed'),
        transactionError: t('messages:errors.transaction-error'),
        tryAgain: t('messages:errors.general-try-again'),
      },
      async () => {
        await sleep(2000)
        void invalidateWalletTokens()
        void refetchOpenOrders()
        void refetchUnsettledBalances()
      },
    )
  }, [
    baseToken?.decimals,
    baseToken.mint,
    baseToken?.token_program,
    invalidateWalletTokens,
    isBid,
    limitForm.inputAmount,
    limitForm.outputAmount,
    limitForm.price,
    manifestMarket,
    quoteToken?.decimals,
    quoteToken.mint,
    quoteToken?.token_program,
    refetchOpenOrders,
    refetchUnsettledBalances,
    t,
    wallet,
  ])

  const showOrderDescription = !!(
    limitForm.inputAmount &&
    limitForm.outputAmount &&
    limitForm.price
  )

  const orderDescription = showOrderDescription
    ? t('limit.order-description', {
        action: isBid ? t('limit.buying') : t('limit.selling'),
        amount: isBid ? limitForm.outputAmount : limitForm.inputAmount,
        token: isBid ? outputToken?.symbol : inputToken?.symbol,
        quoteAmount: isBid ? limitForm.inputAmount : limitForm.outputAmount,
        quoteToken: isBid ? inputToken?.symbol : outputToken?.symbol,
        price: floorToDecimal(limitForm.price, tickDecimals).toFixed(),
        quoteSymbol: quoteToken?.symbol,
        baseSymbol: baseToken?.symbol,
      })
    : ''

  const handlePriceChangePercentage = useCallback(
    (percentage: string) => {
      if (!lastPrice || !percentage) {
        set((state) => {
          state.trade.limitForm.price = ''
        })
      } else {
        const lastPriceDecimal = new Decimal(lastPrice)
        const priceChangeDecimal = lastPriceDecimal.mul(
          new Decimal(percentage).div(100),
        )
        const price = lastPriceDecimal.plus(priceChangeDecimal)
        updatePriceAndCalculateOutput(price.toString())
      }
    },
    [lastPrice, updatePriceAndCalculateOutput],
  )

  return (
    <div className="px-6 py-4">
      <LimitSellInput
        max={walletMax}
        decimals={sellDecimals}
        tickDecimals={tickDecimals}
        lastPrice={lastPrice}
        error={error}
        setError={setError}
      />
      <div className="flex items-center">
        <div className="h-px w-full bg-th-bkg-4" />
        <button
          className="flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-th-active bg-th-bkg-1 md:hover:bg-th-bkg-3"
          onClick={handleSwitchTokens}
        >
          <ArrowsUpDownIcon
            className={`size-5 text-th-active ${
              animateSwitchArrow % 2 == 0 ? 'rotate-0' : 'rotate-180'
            }`}
          />
        </button>
        <div className="h-px w-full bg-th-bkg-4" />
      </div>
      <LimitBuyInput error={error} setError={setError} />
      <div className="mt-2 border-t border-th-bkg-4 pt-3">
        <div className="flex items-center justify-between">
          <p className="mb-1.5 text-xs text-th-fgd-2">
            {t('limit.limit-price', { symbol: quoteToken?.symbol })}
          </p>
          <div
            className={`flex items-center ${
              isBid
                ? priceDifferencePercentage <= 0
                  ? 'text-th-up'
                  : 'text-th-down'
                : priceDifferencePercentage <= 0
                  ? 'text-th-down'
                  : 'text-th-up'
            }`}
          >
            {priceDifferencePercentage >= 0 ? (
              <PlusIcon className="size-3" />
            ) : (
              <MinusIcon className="size-3" />
            )}
            <span className="text-xs">
              {Math.abs(priceDifferencePercentage).toFixed(1)}%
            </span>
          </div>
        </div>
        <div className="relative">
          <div className="absolute left-3 top-1/2 size-6 shrink-0 -translate-y-1/2">
            <ImageWithFallback
              width={24}
              height={24}
              src={quoteToken?.image_url}
              alt={`${quoteToken?.symbol} token logo`}
            />
          </div>
          <NumericFormat
            inputMode="decimal"
            lang="en"
            allowedDecimalSeparators={['.', ',']}
            thousandSeparator=","
            allowNegative={false}
            valueIsNumericString
            decimalScale={tickDecimals}
            name="price"
            id="price"
            className="mb-2 flex h-10 w-full items-center rounded-xl bg-th-bkg-3 px-4 pl-[44px] font-mono text-xl text-th-fgd-1 focus:border-th-fgd-4 focus:outline-none md:hover:border-th-fgd-4 md:hover:focus-visible:border-th-fgd-4"
            placeholder="0.00"
            value={limitForm.price}
            onValueChange={handlePriceChange}
          />
        </div>
        <SizeButtons
          onClick={handlePriceChangePercentage}
          showClear={!!limitForm.price}
          sizeValues={priceChangeValues}
        />
      </div>
      {wallet.connected ? (
        <button
          className={`mt-6 flex h-12 w-full items-center justify-center rounded-full ${
            isBid
              ? 'bg-th-up md:hover:bg-th-up-dark'
              : 'bg-th-down md:hover:bg-th-down-dark'
          } font-bold text-th-button-text focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 md:hover:bg-th-button-hover`}
          // disabled={disabled}
          onClick={placeOrder}
        >
          <span>{t('limit.place-order')}</span>
        </button>
      ) : (
        <ConnectButton
          className="mt-4 flex w-full items-center justify-center"
          isLarge
          isSecondary
        />
      )}
      {error ? (
        <div className="flex justify-center pt-2">
          <InlineNotification
            type="error"
            desc={error}
            hideBorder
            hidePadding
          />
        </div>
      ) : null}
      {showOrderDescription ? (
        <div className="mx-auto mt-6 w-max cursor-help">
          <Tooltip content={orderDescription} placement="bottom">
            <div className="flex items-center gap-1">
              <InformationCircleIcon className="size-4 text-th-fgd-3" />
              <p className="text-xs text-th-fgd-2">
                {t('limit.order-details')}
              </p>
            </div>
          </Tooltip>
        </div>
      ) : null}
    </div>
  )
}

export default LimitForm
